import React, { useCallback } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import ServicaCallOveviewModal from 'components/ServiceCalls/OverviewModal'
import { CloseButton, BaseModalV3 } from 'components/ui'
import { SkeletonBlock } from 'components/NewTaskDashboard/common'
import { RootState } from 'constants/interfaces'
import { setNotificationServiceCallModal } from 'redux/actions/serviceCall'

const ServiceCallModalSkeleton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <SkeletonBlock h="16px" w="38%" borderRadius="4px" />
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Box px="25px" py="8px">
        <SkeletonBlock h="18px" w="60%" borderRadius="4px" mb="16px" />
        <SkeletonBlock h="14px" w="94%" borderRadius="4px" mb="9px" />
        <SkeletonBlock h="14px" w="88%" borderRadius="4px" mb="9px" />
        <SkeletonBlock h="14px" w="66%" borderRadius="4px" />
      </Box>
    }
    footer={<SkeletonBlock h="42px" w="100%" borderRadius="10px" />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

const ContentUnavailable: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <Text fontSize="16px" fontWeight={700} color="#0F172A">
          Notification
        </Text>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Flex h="100%" minH="320px" alignItems="center" justifyContent="center" flexDirection="column" textAlign="center">
        <Text fontSize="15px" fontWeight={700} color="#0F172A">
          Content is no longer available
        </Text>
        <Text fontSize="13px" color="#64748B" mt="8px" maxW="320px">
          It may have been deleted, moved, or you may no longer have access.
        </Text>
      </Flex>
    }
    footer={<Box />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

// Mounted once at app root (see App.tsx), same pattern as TaskOverviewModalHost /
// NotificationPostModalHost / NotificationChatModalHost — lets a service_calls notification open
// the exact call in a modal from any page instead of navigating to /service-calls. The resolver
// (serviceCallsResolver.ts) owns fetching the record and dispatching this state; this host is purely
// presentational over it.
export const NotificationServiceCallModalHost: React.FC = () => {
  const dispatch = useDispatch()
  const modal = useSelector((state: RootState) => state.serviceCalls.notificationServiceCallModal)

  const handleClose = useCallback(() => {
    dispatch(setNotificationServiceCallModal(null))
  }, [dispatch])

  if (!modal) return null
  if (modal.error) return <ContentUnavailable onClose={handleClose} />
  if (modal.isLoading) return <ServiceCallModalSkeleton onClose={handleClose} />

  return (
    <ServicaCallOveviewModal
      isOpen={true}
      onClose={handleClose}
      serviceCallViewId={modal.viewId}
      serviceCallNumber={modal.serviceCallNumber}
    />
  )
}
